import React, { useEffect, useState } from "react";
import axios from "axios";
import SourceScopeEditor from "../SourceScopeEditor";
import LoadingSpinner from "../../common/LoadingSpinner";
import { formatInUserTimezone } from "../../../utils/timezone";

const API_URL = process.env.REACT_APP_API_URL || "/api";
const AVAILABLE_SOURCES = ["Chameleon", "FavorDrop", "DFC", "None"];

function SftpEndpointsTab() {
  const [loading, setLoading] = useState(true);
  const [endpoints, setEndpoints] = useState([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_URL}/admin/integrations/sftp-endpoints`);
      setEndpoints(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load SFTP endpoints");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setDialog({
      label: "",
      host: "",
      port: 22,
      username: "",
      authMethod: "password",
      authSecret: "",
      inboundPath: "/crm/inbound",
      outboundPath: "/crm/outbound",
      processedPath: "/crm/processed",
      failedPath: "/crm/failed",
      filePattern: "",
      pollingIntervalSeconds: 300,
      scopeMode: "all",
      scopeSources: [],
      conflictPolicy: "queue",
      enabled: true,
    });
  };

  const openEdit = (ep) => {
    setEditing(ep);
    setDialog({
      label: ep.label || "",
      host: ep.host || "",
      port: ep.port || 22,
      username: ep.username || "",
      authMethod: ep.auth_method || "password",
      authSecret: "",
      inboundPath: ep.inbound_path || "",
      outboundPath: ep.outbound_path || "",
      processedPath: ep.processed_path || "",
      failedPath: ep.failed_path || "",
      filePattern: ep.file_pattern || "",
      pollingIntervalSeconds: ep.polling_interval_seconds || 300,
      scopeMode:
        ep.allowed_lead_sources === null || ep.allowed_lead_sources === undefined
          ? "all"
          : ep.allowed_lead_sources.length === 0
            ? "none"
            : "specific",
      scopeSources: Array.isArray(ep.allowed_lead_sources)
        ? ep.allowed_lead_sources
        : [],
      conflictPolicy: ep.conflict_policy || "queue",
      enabled: ep.enabled !== false,
    });
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const allowedLeadSources =
        dialog.scopeMode === "all"
          ? null
          : dialog.scopeMode === "none"
            ? []
            : dialog.scopeSources;

      const payload = {
        label: dialog.label,
        host: dialog.host,
        port: Number(dialog.port) || 22,
        username: dialog.username,
        authMethod: dialog.authMethod,
        inboundPath: dialog.inboundPath,
        outboundPath: dialog.outboundPath,
        processedPath: dialog.processedPath,
        failedPath: dialog.failedPath,
        filePattern: dialog.filePattern,
        pollingIntervalSeconds: Number(dialog.pollingIntervalSeconds) || 300,
        allowedLeadSources,
        conflictPolicy: dialog.conflictPolicy,
        enabled: dialog.enabled,
      };
      if (dialog.authSecret) payload.authSecret = dialog.authSecret;

      if (editing) {
        await axios.put(
          `${API_URL}/admin/integrations/sftp-endpoints/${editing.id}`,
          payload,
        );
      } else {
        await axios.post(
          `${API_URL}/admin/integrations/sftp-endpoints`,
          payload,
        );
      }
      setDialog(null);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const test = async (id) => {
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/admin/integrations/sftp-endpoints/${id}/test`,
      );
      alert(`Connection OK. Files visible: ${res.data.filesVisible}`);
    } catch (e) {
      setError(e?.response?.data?.error || "Test failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this SFTP endpoint?")) return;
    try {
      await axios.delete(`${API_URL}/admin/integrations/sftp-endpoints/${id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Delete failed");
    }
  };

  if (loading) return <LoadingSpinner message="Loading SFTP endpoints..." />;

  return (
    <div>
      <div className="management-header">
        <div>
          <h2>SFTP Endpoints</h2>
          <p className="text-muted">
            Automated inbound file sync. CRM polls the endpoint and pushes rows
            through the same merge engine.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <i className="fas fa-plus" /> Add SFTP Endpoint
        </button>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {endpoints.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-server" />
          <h3>No SFTP endpoints configured</h3>
          <p>Add one to enable automated file-based sync.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Host</th>
                <th>Inbound Path</th>
                <th>Interval</th>
                <th>Scope</th>
                <th>Last Polled</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((ep) => (
                <tr key={ep.id}>
                  <td><strong>{ep.label}</strong></td>
                  <td>
                    {ep.username}@{ep.host}:{ep.port}
                  </td>
                  <td><code>{ep.inbound_path}</code></td>
                  <td>{ep.polling_interval_seconds}s</td>
                  <td>
                    {ep.allowed_lead_sources === null ||
                    ep.allowed_lead_sources === undefined
                      ? "All"
                      : Array.isArray(ep.allowed_lead_sources) &&
                          ep.allowed_lead_sources.length === 0
                        ? "None"
                        : ep.allowed_lead_sources.join(", ")}
                  </td>
                  <td>
                    {ep.last_polled_at
                      ? formatInUserTimezone(ep.last_polled_at)
                      : "Never"}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        ep.enabled ? "active" : "inactive"
                      }`}
                    >
                      {ep.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => test(ep.id)}
                    >
                      <i className="fas fa-plug" /> Test
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => openEdit(ep)}
                    >
                      <i className="fas fa-edit" /> Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => remove(ep.id)}
                    >
                      <i className="fas fa-trash" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <div
          className="modal-overlay"
          onMouseDown={() => !saving && setDialog(null)}
        >
          <div
            className="modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ maxWidth: 780 }}
          >
            <h2>{editing ? "Edit SFTP Endpoint" : "Add SFTP Endpoint"}</h2>

            <div className="form-row">
              <div className="form-group">
                <label>Label *</label>
                <input
                  className="form-control"
                  value={dialog.label}
                  onChange={(e) =>
                    setDialog({ ...dialog, label: e.target.value })
                  }
                  placeholder="e.g. Partner XYZ DFC Sync"
                />
              </div>
              <div className="form-group">
                <label>Enabled</label>
                <select
                  className="form-control"
                  value={dialog.enabled ? "1" : "0"}
                  onChange={(e) =>
                    setDialog({ ...dialog, enabled: e.target.value === "1" })
                  }
                >
                  <option value="1">Enabled</option>
                  <option value="0">Disabled</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Host *</label>
                <input
                  className="form-control"
                  value={dialog.host}
                  onChange={(e) =>
                    setDialog({ ...dialog, host: e.target.value })
                  }
                  placeholder="sftp.partner-xyz.example"
                />
              </div>
              <div className="form-group">
                <label>Port</label>
                <input
                  type="number"
                  className="form-control"
                  value={dialog.port}
                  onChange={(e) =>
                    setDialog({ ...dialog, port: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Username *</label>
                <input
                  className="form-control"
                  value={dialog.username}
                  onChange={(e) =>
                    setDialog({ ...dialog, username: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Auth Method *</label>
                <select
                  className="form-control"
                  value={dialog.authMethod}
                  onChange={(e) =>
                    setDialog({ ...dialog, authMethod: e.target.value })
                  }
                >
                  <option value="password">Password</option>
                  <option value="ssh_key">SSH Key</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>
                {dialog.authMethod === "ssh_key"
                  ? "Private Key (PEM) *"
                  : "Password *"}
              </label>
              <textarea
                className="form-control"
                rows="3"
                value={dialog.authSecret}
                onChange={(e) =>
                  setDialog({ ...dialog, authSecret: e.target.value })
                }
                placeholder={
                  editing
                    ? "Leave blank to keep current secret"
                    : "Enter credential"
                }
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Inbound Path *</label>
                <input
                  className="form-control"
                  value={dialog.inboundPath}
                  onChange={(e) =>
                    setDialog({ ...dialog, inboundPath: e.target.value })
                  }
                  placeholder="/crm/inbound"
                />
              </div>
              <div className="form-group">
                <label>Processed Path</label>
                <input
                  className="form-control"
                  value={dialog.processedPath}
                  onChange={(e) =>
                    setDialog({ ...dialog, processedPath: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Failed Path</label>
                <input
                  className="form-control"
                  value={dialog.failedPath}
                  onChange={(e) =>
                    setDialog({ ...dialog, failedPath: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Outbound Report Path</label>
                <input
                  className="form-control"
                  value={dialog.outboundPath}
                  onChange={(e) =>
                    setDialog({ ...dialog, outboundPath: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>File Pattern (regex)</label>
                <input
                  className="form-control"
                  value={dialog.filePattern}
                  onChange={(e) =>
                    setDialog({ ...dialog, filePattern: e.target.value })
                  }
                  placeholder="^leads_.*\\.csv$"
                />
              </div>
              <div className="form-group">
                <label>Polling Interval (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  value={dialog.pollingIntervalSeconds}
                  onChange={(e) =>
                    setDialog({
                      ...dialog,
                      pollingIntervalSeconds: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="form-group">
              <label>Source Scope</label>
              <SourceScopeEditor
                mode={dialog.scopeMode}
                sources={dialog.scopeSources}
                available={AVAILABLE_SOURCES}
                onChange={({ mode, sources }) =>
                  setDialog({ ...dialog, scopeMode: mode, scopeSources: sources })
                }
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Conflict Policy</label>
              <select
                className="form-control"
                value={dialog.conflictPolicy}
                onChange={(e) =>
                  setDialog({ ...dialog, conflictPolicy: e.target.value })
                }
              >
                <option value="queue">Queue for manual review (safest)</option>
                <option value="crm_wins">CRM wins</option>
                <option value="external_wins">External wins</option>
              </select>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setDialog(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={saving || !dialog.label || !dialog.host || !dialog.username}
              >
                {saving ? "Saving..." : editing ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SftpEndpointsTab;